import { DEMO_BOARD_MAP_IDS, mapsFromCatalog } from '../maps'
import type { SessionTypeId } from '../sessionTypes'
import type { ScenarioSession } from '../types'

/** Empty Whiteboard seed (legacy key — keep stable for existing boards). */
export const BLANK_BOARD_ID = 'blank-board'
export const BLANK_WALKTHROUGH_ID = 'blank-walkthrough'
export const BLANK_EXERCISE_ID = 'blank-exercise'

/** Library keys for empty type seeds (Set Location unlocked; not authored geography). */
export const BLANK_LIBRARY_TEMPLATE_IDS = [
  BLANK_BOARD_ID,
  BLANK_WALKTHROUGH_ID,
  BLANK_EXERCISE_ID,
] as const

export type BlankLibraryTemplateId = (typeof BLANK_LIBRARY_TEMPLATE_IDS)[number]

/** Silent technical origin for internal story math — not a scheduled/story duration. */
const ORIGIN = '2026-09-22T10:00:00-05:00'

const BLANK_BY_TYPE: Record<SessionTypeId, BlankLibraryTemplateId> = {
  whiteboard: BLANK_BOARD_ID,
  walkthrough: BLANK_WALKTHROUGH_ID,
  exercise: BLANK_EXERCISE_ID,
}

export function blankLibraryIdForType(typeId: SessionTypeId): BlankLibraryTemplateId {
  return BLANK_BY_TYPE[typeId]
}

function createBlankSession(
  id: BlankLibraryTemplateId,
  title: string,
  sessionType: SessionTypeId,
  opts?: { rolePlayEnabled?: boolean },
): ScenarioSession {
  return {
    id,
    title,
    summary: '',
    storyStartTime: ORIGIN,
    timezone: 'America/Chicago',
    showTimeZone: false,
    displayUnits: 'imperial',
    boardComplexity: 'everything',
    sessionType,
    ...(opts?.rolePlayEnabled ? { rolePlayEnabled: true } : {}),
    maps: mapsFromCatalog(DEMO_BOARD_MAP_IDS),
    // Same Streets plate as wizard backdrop / hub park — avoids basemap flash on reselect.
    activeMapId: 'streets',
    // Wide idle framing until Set Location / wizard Where? picks a place (not Yucca).
    camera: { lng: -98.5, lat: 39.8, zoom: 3.4 },
    boardLocationLocked: false,
    comments: [],
    objects: [],
    timeline: [],
  }
}

export function createBlankBoardSession(): ScenarioSession {
  return createBlankSession(BLANK_BOARD_ID, 'Untitled whiteboard', 'whiteboard')
}

export function createBlankWalkthroughSession(): ScenarioSession {
  return createBlankSession(BLANK_WALKTHROUGH_ID, 'Untitled walkthrough', 'walkthrough')
}

export function createBlankExerciseSession(): ScenarioSession {
  return createBlankSession(BLANK_EXERCISE_ID, 'Untitled exercise', 'exercise', {
    rolePlayEnabled: true,
  })
}
